/**
 * useUserData Hook
 *
 * Manages fetching and caching user data for a specific client.
 * Automatically refetches when the client changes.
 *
 * @param {string|null} clientId - Client ID to fetch users for
 * @returns {Object} User data state
 * @returns {Array} users - Array of user objects
 * @returns {boolean} loading - Whether data is currently loading
 * @returns {Error|null} error - Error object if fetch failed
 * @returns {function} refetch - Function to manually trigger a refetch
 */

import { useState, useEffect, useCallback } from 'react';
import { listUsers } from '../services/userService';

export function useUserData(clientId) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch users for the current client
   */
  const fetchUsers = useCallback(async () => {
    // Don't fetch if no clientId
    if (!clientId) {
      setUsers([]);
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data = await listUsers(clientId);
      setUsers(data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // Fetch users when clientId changes
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Refetch function for manual refresh
  const refetch = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  return {
    users,
    loading,
    error,
    refetch,
  };
}
